import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGeri, useDuzenlemeBitti } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Save, Plus, X, Image, Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { trSayi, trPara } from '@/lib/sayi';
import VoiceInput from '@/components/VoiceInput';
import ProductCombobox from '@/components/ProductCombobox';

/**
 * Kayıtlı çıktı birimini, birimler listesindeki yazımına indirger.
 *
 * `units` tablosundaki kısaltmaların TAMAMI küçük harf (adet, kg, lt…) —
 * ölçtük, büyük harfli tek kısaltma yok. Reçetelerde ise "KG"/"ADET" gibi
 * 62 kayıt var (Excel'den gelmiş). Seçim kutusu tam eşitlik aradığı için
 * bunlarda kutu boş açılıyor, kullanıcı birimin silindiğini sanıyordu.
 *
 * Kısaltmalar ASCII olduğu için `toLowerCase()` Türkçe 'İ' tuzağına düşmez.
 * Sunucu artık kaydederken kanonik yazımı yazıyor; bu yalnızca ESKİ kayıtlar
 * içindir.
 */
const birimiEslestir = (b) => (b || '').trim().toLowerCase() || null;

export default function RecipeForm() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('recipes', 'delete');
  const { products } = useProducts();
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/recipes');
  const duzenlemeBitti = useDuzenlemeBitti();
  const isEdit = !!id;
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const photoInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  // Duzenlemeye girildiginde recetenin bagli oldugu urun; degistirilirse
  // kullaniciya tasima uyarisi gostermek icin tutuluyor.
  const [ilkUrunId, setIlkUrunId] = useState('');
  const [form, setForm] = useState({
    product_card_id: '', product_name: '', product_code: '', yield_quantity: '1', yield_unit: 'porsiyon',
    production_type: 'taze', instructions: '', preparation_steps: '', important_notes: '', ingredients: []
  });

  useEffect(() => {
    api.get('/units').then(r => setUnits(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/recipes/${id}`).then(r => {
        const rec = r.data;
        setIlkUrunId(rec.product_card_id || '');
        setForm({
          product_card_id: rec.product_card_id || '',
          product_name: rec.product_name || '',
          product_code: rec.product_code || '',
          yield_quantity: String(rec.yield_quantity || '1'),
          // Kayitli birim, birimler listesindeki yazimla EŞLEŞTIRILIR.
          // Eski kayitlarda "KG"/"ADET" gibi buyuk harfli degerler var;
          // secim kutusu tam esitlik aradigi icin kutu BOS aciliyor ve
          // kullanici birimi kaybolmus saniyordu.
          yield_unit: birimiEslestir(rec.yield_unit) || 'porsiyon',
          production_type: rec.production_type || 'taze',
          instructions: rec.instructions || '',
          preparation_steps: rec.preparation_steps || '',
          important_notes: rec.important_notes || '',
          ingredients: (rec.ingredients || []).map(ing => ({
            product_card_id: ing.product_card_id || '',
            quantity: String(ing.quantity || ''),
            unit: ing.unit || 'kg',
            notes: ing.notes || ''
          }))
        });
        setPhotos((rec.photos || []).map(p => ({ ...p, existing: true })));
      }).catch(() => toast.error('Recete yuklenemedi'));
    }
  }, [id, isEdit]);

  // Çıktı ürünleri: Mamul ve Yarı Mamul
  const outputProducts = products.filter(p => ['finished', 'semi_finished'].includes(p.type));
  // Bileşen ürünleri: DEMİRBAŞ DIŞINDA her şey.
  //
  // Burası ['raw_material','semi_finished'] ile sınırlıydı. Reçetelerde bu
  // ikisinin dışında da malzeme var — masa üstü sosları, kürdan, ıslak mendil
  // gibi `guest_consumption` kalemler ve alt reçete olarak kullanılan
  // `finished` ürünler. Bunlar listede olmadığı için düzenlemeye girildiğinde
  // kutu BOŞ açılıyor, kayıtlı malzeme silinmiş gibi görünüyordu; kaydedilirse
  // gerçekten siliniyordu. Ölçtük: 9 reçetede 11 kalem böyleydi.
  //
  // İzin listesi yerine DIŞLAMA: yeni bir ürün türü eklendiğinde aynı hata
  // tekrar üremesin. Demirbaş bir yemeğin içine giremez, tek dışlanan o.
  const ingredientProducts = products.filter(p => p.type !== 'fixed_asset');

  const groupLabels = { weight: 'Agirlik', volume: 'Hacim', quantity: 'Miktar' };
  const unitsByGroup = {};
  units.forEach(u => {
    if (!unitsByGroup[u.unit_group]) unitsByGroup[u.unit_group] = [];
    unitsByGroup[u.unit_group].push(u);
  });

  const addIngredient = () => {
    setForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { product_card_id: '', quantity: '', unit: 'kg', notes: '' }]
    }));
  };

  const removeIngredient = (index) => {
    setForm(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }));
  };

  const updateIngredient = (index, field, value) => {
    const ings = [...form.ingredients];
    ings[index][field] = value;
    if (field === 'product_card_id') {
      const p = products.find(x => x.id === value);
      if (p) ings[index].unit = p.unit;
    }
    setForm(prev => ({ ...prev, ingredients: ings }));
  };

  const handleVoiceConfirm = (voiceItems) => {
    const newIngs = voiceItems.map(i => ({
      product_card_id: i.matched_product_id,
      quantity: String(i.quantity),
      unit: i.unit || 'kg',
      notes: ''
    }));
    setForm(prev => ({ ...prev, ingredients: [...prev.ingredients, ...newIngs] }));
    setShowVoice(false);
  };

  const handleAISuggest = async () => {
    if (!form.product_card_id) { toast.error('Once urun secin'); return; }
    const selectedProduct = outputProducts.find(p => p.id === form.product_card_id);
    if (!selectedProduct) return;
    setAiLoading(true);
    try {
      const res = await api.post('/recipes/ai-suggest', {
        recipe_name: `${selectedProduct.name} (${selectedProduct.code})`,
        yield_quantity: parseFloat(form.yield_quantity) || 1,
        yield_unit: form.yield_unit || 'porsiyon'
      });
      const data = res.data;

      const newIngs = (data.ingredients || []).map(ing => ({
        product_card_id: ing.product_id || '',
        quantity: String(ing.quantity || ''),
        unit: ing.unit || 'kg',
        notes: ing.notes || '',
        _ai_name: ing.product_name,
        _in_stock: ing.in_stock
      }));

      setForm(prev => ({
        ...prev,
        ingredients: newIngs,
        preparation_steps: data.preparation_steps || prev.preparation_steps,
        important_notes: data.important_notes || prev.important_notes,
        instructions: data.estimated_time_minutes ? `Tahmini sure: ${data.estimated_time_minutes} dakika` : prev.instructions
      }));

      const matched = newIngs.filter(i => i.product_card_id).length;
      const unmatched = newIngs.length - matched;
      toast.success(`${newIngs.length} malzeme olusturuldu (${matched} eslesti, ${unmatched} stokta yok)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI hatasi');
    }
    setAiLoading(false);
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, { file, preview: ev.target.result, caption: '' }]);
      };
      reader.readAsDataURL(file);
    });
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // Convert ingredient quantity to product's main unit using to_base_factor
  const convertToMainUnit = (product, ingUnit, ingQty) => {
    if (!product || !ingQty) return 0;
    const qty = parseFloat(ingQty);
    const mainUnit = units.find(u => u.id === product.unit_id || u.abbreviation === product.unit);
    const fromUnit = units.find(u => u.abbreviation === ingUnit);
    if (!mainUnit || !fromUnit) return qty;
    if (mainUnit.id === fromUnit.id) return qty;

    // Same group: convert via base factor (e.g. g->kg: qty * g.to_base / kg.to_base)
    if (mainUnit.unit_group === fromUnit.unit_group && mainUnit.unit_group !== 'quantity') {
      return qty * fromUnit.to_base_factor / mainUnit.to_base_factor;
    }

    // Different group: check product unit_conversions
    for (const conv of (product.unit_conversions || [])) {
      const convFrom = units.find(u => u.id === conv.from_unit_id);
      if (convFrom && convFrom.abbreviation === ingUnit && conv.factor) {
        return qty * conv.factor;
      }
    }
    return qty; // No conversion found, return as-is
  };

  const ingredientCost = (product, ingUnit, ingQty) => {
    if (!product || !product.last_purchase_price || !ingQty) return 0;
    const qtyInMainUnit = convertToMainUnit(product, ingUnit, ingQty);
    return qtyInMainUnit * product.last_purchase_price;
  };

  const calculateCost = () => {
    let total = 0;
    form.ingredients.forEach(ing => {
      const p = products.find(x => x.id === ing.product_card_id);
      total += ingredientCost(p, ing.unit, ing.quantity);
    });
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_card_id || form.ingredients.length === 0) {
      toast.error('Urun ve en az bir malzeme secin');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        yield_quantity: parseFloat(form.yield_quantity),
        ingredients: form.ingredients.map(i => ({
          product_card_id: i.product_card_id,
          quantity: parseFloat(i.quantity),
          unit: i.unit, notes: i.notes || null
        }))
      };
      if (isEdit) {
        await api.patch(`/recipes/${id}`, payload);
        // Upload only new photos
        for (const photo of photos) {
          if (photo.existing) continue;
          const formData = new FormData();
          formData.append('file', photo.file);
          formData.append('caption', photo.caption || '');
          await api.post(`/recipes/${id}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success('Recete guncellendi');
        duzenlemeBitti(`/erp/recipes/${id}`);
      } else {
        const res = await api.post('/recipes', payload);
        const recipeId = res.data.id;
        for (const photo of photos) {
          if (photo.existing) continue;
          const formData = new FormData();
          formData.append('file', photo.file);
          formData.append('caption', photo.caption || '');
          await api.post(`/recipes/${recipeId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success('Recete olusturuldu');
        navigate(`/erp/recipes/${recipeId}`, { replace: true });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
    setLoading(false);
  };

  const totalCost = calculateCost();
  const yieldQty = parseFloat(form.yield_quantity) || 1;
  const outputUnit = products.find(p => p.id === form.product_card_id)?.unit;

  return (
    <div data-testid="recipe-form-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">{isEdit ? 'Recete Duzenle' : 'Yeni Recete'}</h1>
          <p className="text-sm text-[#8a7e70] mt-0.5">
            {isEdit
              ? 'Düzenleme yeni versiyon açmaz — bu versiyon güncellenir. Yeni versiyon için "Yeni Reçete" kullanın.'
              : 'Bu ürünün reçetesi zaten varsa yeni versiyon olarak eklenir, eskisi pasife alınır.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Recete Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Urun (Cikti) *</Label>
              {/* Duzenlemede de degistirilebilir: yanlis urune baglanmis bir
                  receteyi tasiyabilmek icin. Eskiden salt okunurdu ve tek care
                  silip butun malzemeleri yeniden girmekti. */}
              <ProductCombobox
                value={form.product_card_id}
                products={outputProducts}
                placeholder="Urun secin veya arayin..."
                emptyText="Mamul/yari mamul bulunamadi"
                className="h-10"
                testId="recipe-product-select"
                onChange={(v) => {
                  // Uretim birimi varsayilan olarak urun kartinin birimi olur.
                  // Sabit "porsiyon" varsayimi, karti kg olan urunlerde stok
                  // girisini yanlis birimde yaptiriyordu.
                  const secilen = outputProducts.find(x => x.id === v);
                  setForm(p => ({ ...p, product_card_id: v, yield_unit: secilen?.unit || p.yield_unit }));
                }}
              />
              {isEdit && form.product_card_id !== ilkUrunId && ilkUrunId && (
                <p className="text-xs text-[#7a6a2e]">
                  Cikti urunu degistiriliyor: kaydedince recete bu urune tasinir,
                  malzemeler oldugu gibi kalir.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Uretim Miktari *</Label>
              {/* step="any" ŞART. step="0.01" iken tarayıcı, üç ondalıklı bir
                  değeri (Hamburger Sos 1,085 kg) "adım uyuşmazlığı" sayıp formu
                  GÖNDERMİYOR ve odağı sessizce bu alana atıyordu — hata mesajı
                  bile çıkmıyor. Kullanıcı üretim türünü değiştirip kaydedemiyordu.
                  18 reçetenin üretim miktarı bu durumdaydı. Bunlar ölçülen
                  değerler; sabit bir adıma zorlanamazlar. */}
              <Input type="number" step="any" min="0" value={form.yield_quantity}
                onChange={(e) => setForm(p => ({ ...p, yield_quantity: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="recipe-yield-input" />
              <p className="text-xs text-[#8a7e70]">
                Bu recete bir kez calistiginda cikan urun miktari. Birim maliyet ve
                uretim emri katsayisi buna bolunur.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Uretim Birimi</Label>
              <Select value={form.yield_unit} onValueChange={(v) => setForm(p => ({ ...p, yield_unit: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="recipe-yield-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(unitsByGroup).map(([group, gu]) => (
                    <SelectGroup key={group}>
                      <SelectLabel className="text-xs font-bold uppercase text-[#8a7e70]">{groupLabels[group] || group}</SelectLabel>
                      {gu.map(u => <SelectItem key={u.abbreviation} value={u.abbreviation}>{u.abbreviation} — {u.name}</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {outputUnit && form.yield_unit !== outputUnit && (
                <p className="text-xs text-[#7a6a2e]">
                  Urun kartinin birimi <strong>{outputUnit}</strong>. Farkli bir birim
                  kullanacaksaniz urun kartinda "1 {form.yield_unit} = ? {outputUnit}"
                  donusumu tanimli olmali, aksi halde recete kaydedilemez.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Üretim Türü</Label>
              <Select value={form.production_type} onValueChange={(v) => setForm(p => ({ ...p, production_type: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="recipe-production-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="taze">Taze</SelectItem>
                  <SelectItem value="on_hazirlikli">Ön Hazırlıklı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* AI button - after product selected */}
            {form.product_card_id && !isEdit && (
              <div className="flex items-end">
                <Button type="button" onClick={handleAISuggest} disabled={aiLoading}
                  className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold h-10 px-5"
                  data-testid="ai-generate-btn">
                  {aiLoading ? (
                    <span className="flex items-center gap-2"><Sparkles size={15} className="animate-spin" /> Olusturuluyor...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Sparkles size={15} /> AI ile Malzemeleri Getir</span>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voice Input */}
        {showVoice && (
          <VoiceInput products={ingredientProducts} onConfirm={handleVoiceConfirm} />
        )}

        {/* Ingredients */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Malzemeler</CardTitle>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setShowVoice(!showVoice)} size="sm"
                variant={showVoice ? "default" : "outline"}
                className={showVoice ? "bg-[#7a3535] text-white hover:bg-[#5e2929]" : "border-[#d0c4b0] text-[#3F3E39]"}
                data-testid="toggle-voice-btn">
                {showVoice ? '✕ Sesli Girisi Kapat' : '🎤 Sesli Giris'}
              </Button>
              <Button type="button" onClick={addIngredient} size="sm"
                className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="add-ingredient-btn">
                <Plus size={14} className="mr-1" /> Malzeme Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-28">Miktar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-24">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Maliyet</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.ingredients.map((ing, i) => {
                  const p = products.find(x => x.id === ing.product_card_id);
                  const cost = ingredientCost(p, ing.unit, ing.quantity);

                  // Allowed units for this product: same group as main unit + alternative conversions
                  let allowedUnits = [];
                  if (p) {
                    const mainUnit = units.find(u => u.id === p.unit_id || u.abbreviation === p.unit);
                    if (mainUnit) {
                      // Same group units (e.g. kg->g, lt->ml)
                      allowedUnits = units.filter(u => u.unit_group === mainUnit.unit_group);
                      // Plus alternative conversion units from product
                      (p.unit_conversions || []).forEach(conv => {
                        const altUnit = units.find(u => u.id === conv.from_unit_id);
                        if (altUnit && !allowedUnits.find(a => a.id === altUnit.id)) {
                          allowedUnits.push(altUnit);
                        }
                      });
                    }
                  }
                  if (allowedUnits.length === 0) allowedUnits = units;

                  return (
                    <TableRow key={i} className={`border-[#d0c4b0] ${ing._in_stock === false ? 'bg-[#7a6a2e]/5' : ''}`}>
                      <TableCell>
                        <ProductCombobox
                          value={ing.product_card_id}
                          products={ingredientProducts}
                          placeholder={ing._ai_name || 'Malzeme secin veya arayin...'}
                          emptyText="Malzeme bulunamadi"
                          className="h-9 text-sm"
                          testId={`ingredient-product-${i}`}
                          onChange={(v) => updateIngredient(i, 'product_card_id', v)}
                        />
                        {ing._in_stock === false && !ing.product_card_id && (
                          <p className="text-xs text-[#7a6a2e] mt-1">Stokta yok: {ing._ai_name}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* step="any": 24 malzeme kalemi 0,001'in katı değil
                            (0,0002 / 0,0005 / 0,0228). Aynı sessiz engel. */}
                        <Input type="number" step="any" value={ing.quantity}
                          onChange={(e) => updateIngredient(i, 'quantity', e.target.value)}
                          className="bg-white border-[#d0c4b0]" data-testid={`ingredient-qty-${i}`} />
                      </TableCell>
                      <TableCell>
                        <Select value={ing.unit} onValueChange={(v) => updateIngredient(i, 'unit', v)}>
                          <SelectTrigger className="bg-white border-[#d0c4b0] w-24" data-testid={`ingredient-unit-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {allowedUnits.map(u => (
                              <SelectItem key={u.id} value={u.abbreviation}>{u.abbreviation}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-[#3F3E39]">{trPara(cost)} TL</TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredient(i)}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10" data-testid={`remove-ingredient-${i}`}>
                          <X size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {form.ingredients.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#8a7e70]">Malzeme ekleyin veya sesli giris kullanin</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Cost Summary */}
        <div className="flex items-center justify-between bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
          <div>
            <p className="text-sm text-[#8a7e70]">Toplam Maliyet: <span className="font-bold text-[#1a1916]">{trPara(totalCost)} TL</span></p>
            <p className="text-sm text-[#8a7e70]">Birim Maliyet: <span className="font-bold text-[#1a1916]">{trPara(totalCost / yieldQty)} TL / {form.yield_unit}</span></p>
          </div>
        </div>

        {/* Preparation Steps & Notes */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Tarif ve Notlar</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Yapim Tarifi</Label>
              <Textarea value={form.preparation_steps}
                onChange={(e) => setForm(p => ({ ...p, preparation_steps: e.target.value }))}
                placeholder="Adim adim yapim tarifi... (Markdown destekli: **kalin**, - madde isareti)"
                className="bg-white border-[#d0c4b0] min-h-[120px]" data-testid="recipe-preparation-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Onemli Noktalar</Label>
              <Textarea value={form.important_notes}
                onChange={(e) => setForm(p => ({ ...p, important_notes: e.target.value }))}
                placeholder="Sef notlari, allerjen uyarilari, servis onerileri..."
                className="bg-white border-[#d0c4b0] min-h-[80px]" data-testid="recipe-notes-input" />
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Fotograflar</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={() => photoInputRef.current?.click()}
              className="border-[#d0c4b0] text-[#3F3E39]" data-testid="add-photo-btn">
              <Upload size={14} className="mr-1.5" /> Fotograf Ekle
            </Button>
            <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoAdd} className="hidden" />
          </CardHeader>
          <CardContent className="p-6">
            {photos.length === 0 ? (
              <p className="text-center py-4 text-[#8a7e70] text-sm">Henuz fotograf eklenmedi</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo, i) => (
                  <div key={photo.id || i} className="relative group">
                    <img src={photo.existing ? photo.url : photo.preview} alt="" className="w-full h-32 object-cover rounded-lg border border-[#d0c4b0]" />
                    {!(photo.existing && isEdit && !silmeYetkisi) && (
                    <Button type="button" size="icon" variant="ghost"
                      onClick={async () => {
                        if (photo.existing && isEdit) {
                          await api.delete(`/recipes/${id}/photos/${photo.id}`).catch(() => {});
                        }
                        setPhotos(prev => prev.filter((_, j) => j !== i));
                      }}
                      className="absolute top-1 right-1 h-7 w-7 bg-[#7a3535] text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                      data-testid={`remove-photo-${i}`}>
                      <X size={12} />
                    </Button>
                    )}
                    <Input placeholder="Aciklama" value={photo.caption || ''}
                      onChange={(e) => { const p = [...photos]; p[i] = { ...p[i], caption: e.target.value }; setPhotos(p); }}
                      className="mt-2 text-xs bg-white border-[#d0c4b0]" data-testid={`photo-caption-${i}`} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]" data-testid="cancel-btn">Iptal</Button>
          <Button type="submit" disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="save-recipe-btn">
            <Save size={16} className="mr-2" /> {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
}
